/**
 * Licensed Materials - Property of IBM
 * 5737-M66, 5900-AAA, 5900-A0N, 5725-S86, 5737-I75
 * © Copyright IBM Corp. 2020, 2023 All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication, or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 */

export * from 'exploreConstants';
export * from 'dataConstants';
export * from './CommonPropTypes';
export * from 'DataPropTypes';
export * from 'KPIPropTypes';
export * from '../../Monitor/constants/ExplorePropTypes';
export * from '../../Monitor/constants/monitorConstants';
export * from '../../Dashboard/constants/MonitoringConstants';
export * from '../../Dashboard/constants/MonitoringPropTypes';
export * from '../../Services/constants/Constants';
export * from '../../Users/constants/UserPropTypes';

export const POSTGRESQL_IDENTIFIER_MAXLENGTH = 63;
export const DB2_IDENTIFIER_MAXLENGTH = 255;

export const TEXT_INPUT_TYPES = {
  TEXT: 'TEXT',
  PASSWORD: 'PASSWORD',
  MULTILINE: 'MULTILINE',
};

export const PRODUCT_NAME = 'IBM Maximo Asset Monitor';

export const ROUTES = {
  HOME: '/home',
  USERS: '/users',
  SERVICES: '/services',
  DASHBOARD: '/dashboard',

  CONNECT: '/connect',
  CONNECT_DEVICES: '/connect/devices',
  CONNECT_TYPES: '/connect/types',
  CONNECT_INTERFACES: '/connect/interfaces',

  MONITOR: '/monitor',
  MONITOR_HIERARCHY: '/monitor/hierarchies',
  MONITOR_ASSET_TYPE: '/monitor/asset-types',
  MONITOR_DEVICE_TYPE: '/monitor/device-types',
  MONITOR_ASSETS: '/monitor/assets',

  SETUP: '/setup',
  SETUP_ASSET_TYPE: '/setup/asset-types',
  SETUP_ASSET: '/setup/asset',
  SETUP_DEVICE_TYPE: '/setup/device-types',
  SETUP_HIERARCHY: '/setup/hierarchies',
  SETUP_DEVICE: '/setup/device',
  SETUP_LOCATION_TYPE: '/setup/location-types',
  SETUP_LOCATION: '/setup/location',
  SETUP_HIERARCHY_DETAILS: 'details',

  LOGIN: '/login',
  LOGOUT: '/logout',
  PREAUTH: '/preauth',
  SIGNIN: '/signin',
  TIMEOUT: '/session-timeout',
  UNAUTHORIZED: '/unauthorized',
  NOMATCH: '/nomatch',
  DOCS: '/docs',
};

export const SETUP_ASSETS_URLS = {
  ADD_ASSET: 'add-asset',
  ASSET_TYPE_DETAILS: 'asset-type',
};

export const SETUP_TAB_URLS = {
  ASSETS: 'assets',
  ARCHIVES: 'archives',
  HIERARCHIES: 'hierarchies',
  HIERARCHIES_IMPORT: 'hierarchies/import',
  LOCATIONS: 'locations',
  DEVICES: 'deviceTypes',
};

export const SETUP_ASSET_DETAILS = 'details';

export const SETUP_ASSET_DETAILS_TAB_URLS = {
  IDENTITY: `${SETUP_ASSET_DETAILS}/identity`,
  DATA_ITEMS: `${SETUP_ASSET_DETAILS}/data-items`,
  ASSETS: `${SETUP_ASSET_DETAILS}/assets`,
  DASHBOARDS: `${SETUP_ASSET_DETAILS}/dashboards`,
  DASHBOARD_TEMPLATES: `${SETUP_ASSET_DETAILS}/dashboardTemplates`,
};

export const SETUP_DATA_PANE_TAB_URLS = {
  TREND: 'trend',
  DATA_TABLE: 'data-table',
  AGGREGATION: 'aggregation',
  DEPENDENCIES: 'dependencies',
};

export const MONITOR_PAGE_URLS = {
  DEVICES: 'devices',
  ASSETS: 'assets',
  DASHBOARDS: 'dashboards',
};

export const DATE_RANGES = {
  ONE_HOUR: 'h',
  ONE_DAY: 'd',
  ONE_WEEK: 'w',
  ONE_MONTH: 'M',
  ONE_YEAR: 'y',
};

export const TIME_UNITS = {
  DAYS: 'D',
  HOURS: 'H',
  MINUTES: 'T',
  SECONDS: 'S,',
};

export const DATA_STATES = {
  LOADING: 'LOADING',
  DATA_ERROR: 'DATA_ERROR',
  FUNCTION_ERROR: 'FUNCTION_ERROR',
  NOT_CALCULATED: 'NOT_CALCULATED',
  NOT_CALCULATED_V2: 'NOT_CALCULATED_V2',
  NO_DATA: 'NO_DATA',
  TRANSIENT: 'TRANSIENT',
  TIMESTAMP: 'TIMESTAMP',
};

export const MOMENT_INPUT_FORMATS = {
  LONG: 'LLL',
  DATE_INPUT: 'L',
  TIME_INPUT: 'HH:mm',
  RANGE: 'L HH:mm',
  SECONDS: 'L HH:mm:ss',
};

export const DIALOG_IDS = {
  CREATE_GRAIN_MODAL: 'CREATE_GRAIN_MODAL',
  SAMPLE_APP_LOGS_MODAL: 'SAMPLE_APP_LOGS_MODEL',
  EXTEND_FEATURES_DIALOG: 'EXTEND_FEATURES_DIALOG',
  RESTORE_ENTITY_TYPE_MODAL: 'RESTORE_ENTITY_TYPE_MODAL',
  DELETE_ENTITY_TYPE_MODAL: 'DELETE_ENTITY_TYPE_MODAL',
  DELETE_DASHBOARD_MODAL: 'DELETE_DASHBOARD_MODAL',
  REMOVE_PIN_DASHBOARD: 'REMOVE_PIN_DASHBOARD',
  VIEW_MAXIMO_SERVICES_MODAL: 'VIEW_MAXIMO_SERVICES_MODAL',
  DELETE_OTHER_SERVICES_MODAL: 'DELETE_OTHER_SERVICES_MODAL',
  VIEW_FACTORYTALK_SERVICES_MODAL: 'VIEW_FACTORYTALK_SERVICES_MODAL',
  CREATE_WORKORDER_MODAL: 'CREATE_WORKORDER_MODAL',
  UPLOAD_IMAGE_FOR_ENTITY_MODAL: 'UPLOAD_IMAGE_FOR_ENTITY_MODAL',
  CHANGE_ALERT_STATUS_MODAL: 'CHANGE_ALERT_STATUS_MODAL',
  CHANGE_ALERT_OWNER_MODAL: 'CHANGE_ALERT_OWNER_MODAL',
  ARCHIVE_ASSET_TYPE_MODAL: 'ARCHIVE_ASSET_TYPE_MODAL',
  CREATE_ASSET_DASHBOARD_MODAL: 'CREATE_ASSET_DASHBOARD_MODAL',
  DISABLE_DASHBOARD_MODAL: 'DISABLE_DASHBOARD_MODAL',
  REMOVE_DEVICES_FROM_LOCATION_MODAL: 'REMOVE_DEVICES_FROM_LOCATION_MODAL',
  IMPORT_HIERARCHY_CSV_MODAL: 'IMPORT_HIERARCHY_CSV_MODAL',
};

export const CATEGORY_COLORS = [
  '#3B1A40',
  '#473793',
  '#3C6DF0',
  '#48D4BB',
  '#00A68F',
  '#b4a1a1',
  '#ffb000',
  '#95d13c',
  '#fe8500',
  '#ff509e',
  '#9b82f3',
];

export const LOCAL_STORAGE = {
  INITIAL_URL: 'INITIAL_URL',
};

export const SERVICES = {
  ANALYTICS: 'analytic',
};

export const DEFAULT_LANGUAGE = 'en';
export const DEFAULT_LOCALE = DEFAULT_LANGUAGE;
export const SUPPORTED_LANGUAGES = [
  'cs',
  'da',
  'de',
  'en',
  'es',
  'fi',
  'fr',
  'hr',
  'hu',
  'it',
  'ja',
  'ko',
  'nl',
  'nb',
  'pl',
  'pt-br',
  'sk',
  'sl',
  'sv',
  'tr',
  'zh',
  'zh-tw',
];

export const DATA_TYPES = {
  LITERAL: 'LITERAL',
  BOOLEAN: 'BOOLEAN',
  NUMBER: 'NUMBER',
  TIMESTAMP: 'TIMESTAMP',
};

export const NO_OPENER_REFERRER = 'noopener noreferrer';

export const DEFAULT_ALERT_COLUMN_IDS = [
  'timestamp',
  'name',
  'entityId',
  'owner',
  'severity',
  'domainStatus',
  'workorder',
  'entityType',
  'assetAlias',
];

export const ALERT_ROW_COUNT_OPTIONS = ['10', '20', '30', '40', '50'];

export const DEFAULT_ALERT_TABLE_VIEW = {
  id: 'view-all',
  title: 'Alert table',
  isDefault: false,
  isPublic: true,
  columns: DEFAULT_ALERT_COLUMN_IDS.map(id => ({
    id,
    columnName: id, // TODO: won't this cause translation problems
    isDefault: true,
  })),
  filters: {},
  searchValue: '',
  table: { sort: { columnId: 'timestamp', direction: 'DESC' } },
};

export const MAX_PINNED_DASHBOARDS = 30;

export const HIERARCHY_ITEM_LEVELS = {
  location: 'location',
  asset: 'asset',
  device: 'device',
};

export const HIERARCHY_ITEM_ROUTE_PATHS = {
  workspace: 'workspace',
  organizations: 'organizations',
  sites: 'sites',
  systems: 'systems',
  locationTypes: 'locationTypes',
  locations: 'locations',
  assetTypes: 'assetTypes',
  assets: 'assets',
  deviceTypes: 'deviceTypes',
  devices: 'devices',
};

export const SWAGGER_API_ROUTES = [
  {
    id: 'v2-core',
    values: {
      title: 'Manage core resources',
      description:
        'Manage the core resources of Monitor. The resources include hierarchies, catalog functions, KPIs, function pipelines, dashboards, constants, and grains. Choose the type of nodes to query and then specify a specific node.',
      version: 'v2',
      launchURL:
        '/api/v2/core/api-docs/swagger-ui/index.html?configUrl=/api/v2/core/api-docs/swagger-config#',
    },
  },
  {
    id: 'v2-datalake',
    values: {
      title: 'Query data',
      description:
        'Retrieve data such as metrics, dimensions, alerts, and pipelines log files from the Monitor data lake. Choose the type of nodes to query and then specify a specific node.',
      version: 'v2',
      launchURL:
        '/api/v2/datalake/api-docs/swagger-ui/index.html?configUrl=/api/v2/datalake/api-docs/swagger-config#',
    },
  },
  {
    id: 'v2-service-libarary-query',
    values: {
      title: 'Query data for dashboards',
      description:
        'Retrieve raw or aggregated metric data for nodes from the Monitor data lake for display in dashboards.',
      version: 'v2',
      launchURL: '/api/v2/asengine/api-docs/',
    },
  },
  {
    id: 'v2-service-libarary-image',
    values: {
      title: 'Manage images',
      description:
        'Manage images in the Monitor database. Images are associated with nodes and displayed on image cards on dashboards.',
      version: 'v2',
      launchURL: '/api/v2/images/api-docs/',
    },
  },
  {
    id: 'v1-dsc-ft',
    values: {
      title: 'Manage Data Store Connector / FactoryTalk Integration',
      description:
        'Manage the Data Store Connector, FactoryTalk integration and CSV file upload usecases. Example: Manage Maximo and FactoryTalk service configurations, Upload FactoryTalk service configurations files, Test database connections, Manage DSC configuration for device types.',
      version: 'v1',
      launchURL:
        '/api/dscmanager/v1/api-docs/swagger-ui/index.html?configUrl=/api/dscmanager/v1/api-docs/swagger-config#',
    },
  },
];

export const BATCH_ACTIONS = {
  CHANGE_STATUS: 'CHANGE_STATUS',
  CHANGE_OWNER: 'CHANGE_OWNER',
};

/**
 * Add all the documentation links in the following constant
 *
 * all of the following links are appened at the end of `docURL`
 *
 * example:  if the `docURL` is https://www.ibm.com/docs/en/SSQR84_cd
 *
 * for time series card (TIME_SERIES_CARD) full URL will be  https://www.ibm.com/docs/en/SSQR84_cd/iot/dashboard/line_graph.html
 *
 * then the documentation team redirect it to proper topic url https://www.ibm.com/docs/en/maximo-monitor/continuous-delivery?topic=features-line-graph-cards
 *
 */
export const DOCS_LINKS = {
  HEADERS_WHAT_IS_NEW: '/iot/overview/whats_new_mas.html',
  HEADERS_GETTING_STARTED: '/iot/dashboard/getting_started_mas.html',

  /**
   * all the URLs that belongs to SAAS only
   */
  SAAS: {
    HEADERS_WHAT_IS_NEW: '/iot/overview/whats_new_mon.html',
    IOT_PLATFORM: '/iot/developing/iot_platform_mon.html',
  },

  /**
   * all the Monitor links
   */
  AS_OVERVIEW: '/iot/analytics/as_overview.html',
  AS_REFERENCE: '/iot/analytics/reference/references.html',
  CONSTANT_LEARN_MORE: '/iot/analytics/tutorials/as_adding_constant_tutorial_mas.html',
  ADD_DIMENSIONS: '/iot/analytics/as_add_dimensions_mas.html',
  DEFINE_FILTER_EXPRESSION: '/iot/analytics/as_define_filter_expressions.html',
  SERVICES: {
    ADD_MAXIMO_SERVICE_REQUEST: '/iot/analytics/as_add_service_request.html',
    FACTORY_TALK_LEARN_MORE: '/iot/analytics/as_ft_intro.html',
    FACTORY_TALK_DATA_EXPORT: '/iot/analytics/as_ft_data_historian_send.html',
    FACTORY_TALK_DATA_HISTORIAN_SERVICE: '/iot/analytics/as_ft_data_historian_service.html',
  },
  DASHBOARDS: {
    SIMPLE_BAR_CHART: '/iot/dashboard/custom_dashboard_bar.html#simple',
    GROUPED_BAR_CHART: '/iot/dashboard/custom_dashboard_bar.html#grouped',
    STACKED_BAR_CHART: '/iot/dashboard/custom_dashboard_bar.html#stacked',
    TIME_SERIES_CARD: '/iot/dashboard/line_graph.html',
    VALUE_CARD: '/iot/dashboard/value_card.html',
    TABLE_CARD: '/iot/dashboard/table_card.html',
    IMAGE_CARD: '/iot/dashboard/image_cards.html',
    ALERT_CARD: '/iot/dashboard/alert.html',
    THIRD_PARTY_CARD: '/iot/overview/c_config_csp.html',
  },

  /**
   * all the Platform links
   */
  INTERFACE_LEARN_MORE: '/iot/platform/GA_information_management/ga_im_definitions.html',
  DEVICE_EVENT_LEARN_MORE: '/iot/platform/devices/device_dev_index.html',
};

export const TS_FORMAT = 'YYYY-MM-DD-HH.mm.ss.SSSSSS';
export const DATETIME_LOCAL_MS_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSS'; // Needed for the v2 alert apis

export const NEW_UI =
  'https://masdev.monitor.monitordev5.monitortestcluster-d3bcf8b5e47345c6ea924110d640b819-0000.us-south.containers.appdomain.cloud';
